import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { GamepadIcon, MapPin, CalendarCheck, Settings, Image, Trash2 } from 'lucide-react';
import { cleanupOldReservations } from '../../lib/cleanupReservations';
import { toast } from 'sonner';
import { db } from '../../lib/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

export default function AdminDashboard() {
  const { user, userData, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [cleaningUp, setCleaningUp] = useState(false);
  const [stats, setStats] = useState({
    games: 0,
    branches: 0,
    pendingReservations: 0
  });

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) {
      navigate('/login');
    }
  }, [user, loading, isAdmin, navigate]);

  // Auto-cleanup on dashboard load (once per session)
  useEffect(() => {
    const hasCleanedThisSession = sessionStorage.getItem('reservations_cleaned');
    if (isAdmin && !hasCleanedThisSession) {
      handleAutoCleanup();
      sessionStorage.setItem('reservations_cleaned', 'true');
    }
  }, [isAdmin]);

  // Real-time stats
  useEffect(() => {
    if (!isAdmin) return;

    // Games count
    const gamesUnsub = onSnapshot(collection(db, 'games'), (snapshot) => {
      setStats(prev => ({ ...prev, games: snapshot.size }));
    });

    // Branches count
    const branchesUnsub = onSnapshot(collection(db, 'branches'), (snapshot) => {
      setStats(prev => ({ ...prev, branches: snapshot.size }));
    });

    // Pending reservations count
    const pendingQuery = query(
      collection(db, 'reservations'),
      where('status', '==', 'pending')
    );
    const pendingUnsub = onSnapshot(pendingQuery, (snapshot) => {
      setStats(prev => ({ ...prev, pendingReservations: snapshot.size }));
    });

    return () => {
      gamesUnsub();
      branchesUnsub();
      pendingUnsub();
    };
  }, [isAdmin]);

  const handleAutoCleanup = async () => {
    try {
      const result = await cleanupOldReservations();
      if (result.success && result.deletedCount > 0) {
        console.log(`Auto-cleanup: ${result.deletedCount} old reservations removed`);
      }
    } catch (error) {
      console.error('Auto-cleanup error:', error);
    }
  };

  const handleManualCleanup = async () => {
    setCleaningUp(true);
    try {
      const result = await cleanupOldReservations();
      if (result.success) {
        toast.success(`${result.deletedCount} köhnə rezervasiya silindi`);
      } else {
        toast.error('Xəta baş verdi');
      }
    } catch (error) {
      console.error('Cleanup error:', error);
      toast.error('Xəta baş verdi');
    } finally {
      setCleaningUp(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050000] pt-20 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#D90429] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const adminMenuItems = [
    {
      title: 'Bannerlər',
      description: 'Ana səhifə bannerini idarə et',
      icon: Image,
      path: '/admin/banners',
      color: 'from-purple-600 to-purple-800'
    },
    {
      title: 'Oyunlar',
      description: 'Oyunları əlavə et, redaktə et və sil',
      icon: GamepadIcon,
      path: '/admin/games',
      color: 'from-red-600 to-red-800'
    },
    {
      title: 'Filiallar',
      description: 'Filialları idarə et',
      icon: MapPin,
      path: '/admin/branches',
      color: 'from-blue-600 to-blue-800'
    },
    {
      title: 'Rezervasiyalar',
      description: 'Rezervasiyaları təsdiqlə və ya rədd et',
      icon: CalendarCheck,
      path: '/admin/reservations',
      color: 'from-green-600 to-green-800'
    }
  ];

  return (
    <div className="min-h-screen bg-[#050000] pt-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-2">
            <Settings className="w-8 h-8 text-[#D90429]" />
            <h1 className="font-cinzel text-3xl md:text-4xl font-bold text-white">
              Admin Panel
            </h1>
          </div>
          <p className="text-gray-400">
            Xoş gəldiniz, {userData?.name}. Phobia Games idarəetmə paneli.
          </p>
          
          {/* Cleanup Button */}
          <button
            onClick={handleManualCleanup}
            disabled={cleaningUp}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-4 h-4" />
            {cleaningUp ? 'Təmizlənir...' : 'Köhnə rezervasiyaları sil'}
          </button>
        </motion.div>

        {/* Stats - Real-time */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
          {[
            { label: 'Aktiv Oyunlar', value: stats.games, icon: GamepadIcon },
            { label: 'Filiallar', value: stats.branches, icon: MapPin },
            { label: 'Gözləyən Rezervasiyalar', value: stats.pendingReservations, icon: CalendarCheck }
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-[#0F0F0F] border border-white/10 rounded-lg p-4 sm:p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs sm:text-sm">{stat.label}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-white mt-1">{stat.value}</p>
                </div>
                <stat.icon className="w-8 h-8 sm:w-10 sm:h-10 text-[#D90429]/50" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Menu Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {adminMenuItems.map((item, index) => (
            <motion.div
              key={item.path}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
            >
              <Link to={item.path}>
                <div className="bg-[#0F0F0F] border border-white/10 rounded-lg p-6 sm:p-8 hover:border-[#D90429]/50 transition-all group h-full">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform`}>
                    <item.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <h3 className="font-cinzel text-lg sm:text-xl font-bold text-white mb-2 group-hover:text-[#D90429] transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-400">{item.description}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
